import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MdApartment, MdCheck, MdExpandMore } from "react-icons/md";
import { toast } from "react-toastify";
import { clearPermissionCache } from "../../auth/PermissionProvider";
import { Organizacoes } from "../../interfaces/Identidade";
import { getOrganizacoes, trocarOrganizacao } from "../../services/identidadeApi";
import { saveSession } from "../../services/session";

const PANEL_WIDTH = 256;
const PANEL_MAX_HEIGHT = 288;
const GAP = 8;

/**
 * Troca de organização para quem pertence a 2 ou mais. Com 0 ou 1 organização não renderiza nada:
 * não há o que escolher e o controle só ocuparia a barra.
 *
 * O painel sai em portal com `position: fixed` (regra de camada do CLAUDE.md): a navbar tem
 * `backdrop-blur`, que cria contexto de empilhamento e recortaria um painel `absolute`.
 */
const OrganizacaoSelector: React.FC = () => {
  const [dados, setDados] = useState<Organizacoes | null>(null);
  const [aberto, setAberto] = useState(false);
  const [trocando, setTrocando] = useState(false);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number }>({ left: 0 });
  const gatilhoRef = useRef<HTMLButtonElement>(null);
  const painelRef = useRef<HTMLDivElement>(null);

  // Carrega uma vez. Falha aqui não deve atrapalhar a navbar: sem lista, o seletor some.
  useEffect(() => {
    let ativo = true;
    getOrganizacoes()
      .then((r) => ativo && setDados(r))
      .catch((e) => console.error("Não foi possível carregar as organizações.", e));
    return () => {
      ativo = false;
    };
  }, []);

  const posicionar = useCallback(() => {
    const el = gatilhoRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const left = Math.max(GAP, Math.min(r.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - GAP));
    const cabeAbaixo = window.innerHeight - r.bottom >= PANEL_MAX_HEIGHT + GAP;
    setPos(
      cabeAbaixo
        ? { top: r.bottom + GAP, left }
        : { bottom: window.innerHeight - r.top + GAP, left }
    );
  }, []);

  useEffect(() => {
    if (!aberto) return;
    posicionar();
    const fora = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (gatilhoRef.current?.contains(alvo) || painelRef.current?.contains(alvo)) return;
      setAberto(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setAberto(false);
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    window.addEventListener("scroll", posicionar, true);
    window.addEventListener("resize", posicionar);
    return () => {
      document.removeEventListener("mousedown", fora);
      document.removeEventListener("keydown", esc);
      window.removeEventListener("scroll", posicionar, true);
      window.removeEventListener("resize", posicionar);
    };
  }, [aberto, posicionar]);

  if (!dados || dados.organizacoes.length < 2) return null;

  const atual = dados.organizacoes.find((o) => o.clientId === dados.atual);

  const escolher = async (clientId: number) => {
    if (clientId === dados.atual || trocando) {
      setAberto(false);
      return;
    }
    try {
      setTrocando(true);
      const resposta = await trocarOrganizacao(clientId);
      saveSession(resposta.jwtToken, resposta.refreshToken);
      // As permissões são cacheadas por e-mail; a organização nova pode mudar o que a pessoa vê.
      clearPermissionCache();
      // Recarrega a rota atual: cada tela busca os dados do escopo do token, que acabou de mudar.
      window.location.reload();
    } catch (error: any) {
      console.error("Erro ao trocar de organização.", error);
      const msg =
        error?.response?.status === 403
          ? "Você não tem acesso ativo a essa organização."
          : "Não foi possível trocar de organização. Tente novamente.";
      toast.error(msg, { position: "top-right", autoClose: 4000 });
      setTrocando(false);
    }
  };

  return (
    <>
      <button
        ref={gatilhoRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={aberto}
        aria-label="Trocar de organização"
        onClick={() => setAberto((v) => !v)}
        disabled={trocando}
        className="flex max-w-[180px] items-center gap-1.5 rounded-full px-2 py-1 text-sm font-semibold text-navy-700 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 disabled:opacity-60 dark:text-white dark:hover:bg-navy-700"
      >
        <MdApartment className="h-5 w-5 shrink-0 text-gray-600 dark:text-white" aria-hidden="true" />
        <span className="hidden truncate sm:inline">{atual?.nome ?? "Organização"}</span>
        <MdExpandMore className="h-4 w-4 shrink-0 text-gray-600 dark:text-white" aria-hidden="true" />
      </button>

      {aberto &&
        createPortal(
          <div
            ref={painelRef}
            role="listbox"
            aria-label="Organizações"
            style={{ position: "fixed", top: pos.top, bottom: pos.bottom, left: pos.left, width: PANEL_WIDTH, maxHeight: PANEL_MAX_HEIGHT }}
            className="z-[1000] overflow-y-auto rounded-[20px] border border-gray-100 bg-white p-2 shadow-dv-md dark:border-white/10 dark:bg-navy-800 dark:shadow-none"
          >
            <p className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
              Organização
            </p>
            {dados.organizacoes.map((o) => {
              const selecionada = o.clientId === dados.atual;
              return (
                <button
                  key={o.clientId}
                  type="button"
                  role="option"
                  aria-selected={selecionada}
                  onClick={() => escolher(o.clientId)}
                  className="flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-sm text-gray-800 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500/40 dark:text-white dark:hover:bg-navy-700"
                >
                  <span className={`truncate ${selecionada ? "font-bold" : ""}`}>{o.nome}</span>
                  {selecionada && (
                    <MdCheck className="h-4 w-4 shrink-0 text-dv-blue-500" aria-label="Organização atual" />
                  )}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

export default OrganizacaoSelector;
